import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowRight, CheckCircle2, Wifi, ShieldCheck, Headset } from 'lucide-react';
import { portalApi, apiError, PORTAL_TOKEN_KEY } from './api';
import { ErrorBox } from './ui';
import { NetixLogo } from './brand';
import { PasswordHint, passwordProblem } from './password';

const Shell = ({ title, subtitle, children, footer, welcome = 'Welcome back to Netix Fibernet' }) => (
  <div className="portal-root min-h-screen flex portal-auth-bg relative overflow-hidden" data-testid="portal-auth-page">
    <div className="absolute inset-0 portal-auth-grid pointer-events-none" />
    <div className="hidden lg:flex w-[46%] relative text-white p-12 flex-col justify-between">
      <div className="relative">
        <NetixLogo className="h-14" testId="auth-logo-desktop" />
        <p className="mt-10 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold tracking-wide text-blue-100" data-testid="auth-welcome-tag"><Wifi className="h-3.5 w-3.5 text-emerald-300" />{welcome}</p>
        <h2 className="mt-5 text-4xl xl:text-5xl font-extrabold leading-tight">Your connection,<br />fully in your hands.</h2>
        <p className="mt-4 text-blue-100 max-w-md">Live connection status, uptime history, service details, complaints and announcements — all in one secure customer portal.</p>
      </div>
      <div className="relative grid grid-cols-3 gap-3 portal-float">
        {[{ i: ShieldCheck, t: 'ISO 27001 secured' }, { i: Headset, t: '24×7 support desk' }, { i: CheckCircle2, t: 'Real monitoring data' }].map(({ i: I, t }) => (
          <div key={t} className="portal-glass rounded-2xl p-4 text-slate-900 shadow-2xl"><I className="h-5 w-5 text-[#C8102E]" /><p className="mt-2 text-xs font-bold">{t}</p></div>
        ))}
      </div>
      <p className="relative text-xs text-blue-200/70">© {new Date().getFullYear()} Netix World Internet Services Pvt. Ltd. · care@netixfibernet.com · +91 93199 19988</p>
    </div>
    <div className="flex-1 flex items-center justify-center p-6 relative">
      <div className="w-full max-w-md portal-auth-card bg-white dark:bg-slate-900 rounded-3xl p-8 md:p-10">
        <div className="flex items-center justify-between mb-6">
          <NetixLogo className="h-9" chip={false} testId="auth-logo-card" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Customer Portal</span>
        </div>
        <p className="text-sm font-semibold text-[#C8102E]" data-testid="auth-welcome-message">{welcome}</p>
        <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white mt-1">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>}
        {!subtitle && <div className="mb-6" />}
        {children}
        {footer && <div className="mt-6 text-sm text-slate-500 text-center">{footer}</div>}
      </div>
    </div>
  </div>
);

const Input = ({ icon: Icon, testId, ...props }) => (
  <div className="relative">
    {Icon && <Icon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
    <input {...props} data-testid={testId} className={`w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white py-3 ${Icon ? 'pl-10' : 'pl-3'} pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]/40 focus:border-[#C8102E]`} />
  </div>
);

const Submit = ({ loading, children, testId }) => (
  <button type="submit" disabled={loading} data-testid={testId} className="w-full rounded-xl bg-gradient-to-r from-[#C8102E] to-[#9E0B22] text-white font-bold py-3 flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-red-500/30 transition-all disabled:opacity-60">
    {loading ? 'Please wait…' : children}{!loading && <ArrowRight className="h-4 w-4" />}
  </button>
);

export const PortalLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flash = location.state?.flash;
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (localStorage.getItem(PORTAL_TOKEN_KEY)) navigate('/customer-portal'); }, [navigate]);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const { data } = await portalApi.post('/api/portal/auth/login', form);
      localStorage.setItem(PORTAL_TOKEN_KEY, data.access_token);
      navigate('/customer-portal');
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Sign in to your account" subtitle="Use the email and password registered with your Netix Fibernet customer portal." footer={<>New here? <Link className="text-[#C8102E] font-semibold" to="/customer-portal/register" data-testid="go-register-link">Create your portal account</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        {flash && <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="login-flash">{flash}</div>}
        <Input icon={Mail} type="email" placeholder="Registered email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="portal-login-email" />
        <Input icon={Lock} type="password" placeholder="Password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="portal-login-password" />
        <div className="flex justify-end"><Link to="/customer-portal/forgot-password" className="text-xs text-slate-500 hover:text-[#C8102E]" data-testid="forgot-password-link">Forgot password?</Link></div>
        <ErrorBox message={error} testId="portal-login-error" />
        <Submit loading={loading} testId="portal-login-submit">Sign in</Submit>
      </form>
    </Shell>
  );
};

export const PortalRegister = () => {
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError('');
    const p = passwordProblem(form.password); if (p) { setError(p); return; }
    setLoading(true);
    try { const { data } = await portalApi.post('/api/portal/auth/register', form); setDone(data); }
    catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  if (done) return (
    <Shell welcome="Welcome to Netix Fibernet" title="Verify your email" subtitle={`We sent a verification link to ${form.email}.`} footer={<Link className="text-[#C8102E] font-semibold" to="/customer-portal/login">Back to login</Link>}>
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="register-success">
        {done.message} {done.linked_to_service ? 'Your existing Netix service was found and linked automatically.' : 'No service matched this email yet — our team will link your connection after verification.'}
      </div>
    </Shell>
  );
  return (
    <Shell welcome="Welcome to Netix Fibernet" title="Create portal account" subtitle="Use the email registered with Netix Fibernet so we can link your connection." footer={<>Already registered? <Link className="text-[#C8102E] font-semibold" to="/customer-portal/login">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <Input icon={User} placeholder="Full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testId="register-name" />
        <Input icon={Mail} type="email" placeholder="Registered email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testId="register-email" />
        <Input icon={Phone} placeholder="Mobile number" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} testId="register-mobile" />
        <Input icon={Lock} type="password" placeholder="Password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="register-password" />
        <PasswordHint value={form.password} />
        <ErrorBox message={error} testId="register-error" />
        <Submit loading={loading} testId="register-submit">Create account</Submit>
      </form>
    </Shell>
  );
};

export const PortalVerify = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, ok: false, message: '' });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    portalApi.get(`/api/portal/auth/verify?token=${params.get('token')}`).then(({ data }) => setState({ loading: false, ok: true, message: data.message }))
      .catch((e) => setState({ loading: false, ok: false, message: apiError(e) }));
  }, [params]);
  return (
    <Shell welcome="Welcome to Netix Fibernet" title="Email verification" footer={<Link className="text-[#C8102E] font-semibold" to="/customer-portal/login" data-testid="verify-go-login">Go to login</Link>}>
      {state.loading ? <p className="text-sm text-slate-500">Verifying…</p> : (
        <div className={`rounded-xl p-4 text-sm ${state.ok ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="verify-result">{state.message}</div>
      )}
    </Shell>
  );
};

export const PortalForgot = () => {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const { data } = await portalApi.post('/api/portal/auth/forgot-password', { email }); setMsg(data.message); }
    catch (err) { setMsg(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Reset password" subtitle="Enter your registered email and we will send a secure reset link." footer={<Link className="text-[#C8102E] font-semibold" to="/customer-portal/login">Back to login</Link>}>
      <form onSubmit={submit} className="space-y-4">
        <Input icon={Mail} type="email" placeholder="Registered email" required value={email} onChange={(e) => setEmail(e.target.value)} testId="forgot-email" />
        {msg && <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800" data-testid="forgot-message">{msg}</div>}
        <Submit loading={loading} testId="forgot-submit">Send reset link</Submit>
      </form>
    </Shell>
  );
};

export const PortalReset = ({ activate = false }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirm: '', mobile: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    const p = passwordProblem(form.password); if (p) { setError(p); return; }
    setLoading(true); setError('');
    try {
      const url = activate ? '/api/portal-admin/public/activate' : '/api/portal/auth/reset-password';
      const body = { token: params.get('token'), password: form.password, ...(activate && form.mobile ? { mobile: form.mobile } : {}) };
      await portalApi.post(url, body);
      navigate('/customer-portal/login', { state: { flash: activate ? 'Account activated. Please log in.' : 'Password updated. Please log in.' } });
    } catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <Shell welcome={activate ? 'Welcome to Netix Fibernet' : 'Welcome back to Netix Fibernet'} title={activate ? 'Activate your account' : 'Choose a new password'} subtitle={activate ? 'Set a password to finish activating your Netix customer portal.' : undefined}>
      <form onSubmit={submit} className="space-y-4">
        {activate && <Input icon={Phone} placeholder="Mobile number (optional)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} testId="activate-mobile" />}
        <Input icon={Lock} type="password" placeholder="New password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="reset-password" />
        <PasswordHint value={form.password} />
        <Input icon={Lock} type="password" placeholder="Confirm password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} testId="reset-confirm" />
        <ErrorBox message={error} testId="reset-error" />
        <Submit loading={loading} testId="reset-submit">{activate ? 'Activate account' : 'Update password'}</Submit>
      </form>
    </Shell>
  );
};
